// src/pages/DebtStudio/components/Issues/DealStructure/WrapCashflowModal.jsx

import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import ExcelGrid from "components/ExcelGrid";
import ModalBackdrop from "components/ModalBackdrop";

function num(x, dflt = null) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function isBlankRow(r) {
  const d = String(r?.date ?? "").trim();
  const t = r?.total;
  const tBlank =
    t == null || (typeof t === "string" && t.trim() === "") || t === "";
  return !d && tBlank;
}

function makeRow(partial = {}, seed = 0) {
  return {
    id: Date.now() + seed + Math.floor(Math.random() * 1000),
    date: partial.date ?? "",
    total: partial.total != null ? partial.total : "",
  };
}

function toSortDate(value) {
  const s = String(value || "").trim();
  if (!s) return "";

  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const [, y, m, d] = iso;
    return `${y}-${String(Number(m)).padStart(2, "0")}-${String(Number(d)).padStart(2, "0")}`;
  }

  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (us) {
    const [, m, d, yRaw] = us;
    const y = yRaw.length === 2 ? `20${yRaw}` : yRaw;
    return `${y}-${String(Number(m)).padStart(2, "0")}-${String(Number(d)).padStart(2, "0")}`;
  }

  return s;
}

function normalizeIncomingRows(rows) {
  const arr = Array.isArray(rows) ? rows : [];

  const out = arr.map((r, i) => ({
    id: r?.id || Date.now() + i,
    date: r?.date || "",
    total: r?.total != null ? r.total : "",
  }));

  return out.length ? out : [makeRow()];
}

function normalizeRowsForSave(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => ({
      date: toSortDate(r?.date || ""),
      total: num(r?.total, null),
    }))
    .filter((r) => r.date || r.total != null)
    .map((r) => ({
      date: r.date,
      total: r.total ?? 0,
    }))
    .sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;

      return a.date.localeCompare(b.date);
    });
}

function schedulesEquivalent(a, b) {
  const A = Array.isArray(a) ? a : [];
  const B = Array.isArray(b) ? b : [];

  const ABlank = A.length === 0 || (A.length === 1 && isBlankRow(A[0]));
  const BBlank = B.length === 0 || (B.length === 1 && isBlankRow(B[0]));

  if (ABlank && BBlank) return true;

  if (A.length !== B.length) return false;

  for (let i = 0; i < A.length; i++) {
    const ad = String(A[i]?.date ?? "").trim();
    const bd = String(B[i]?.date ?? "").trim();

    if (ad !== bd) return false;

    const at =
      A[i]?.total == null || A[i]?.total === ""
        ? ""
        : String(Number(A[i].total));

    const bt =
      B[i]?.total == null || B[i]?.total === ""
        ? ""
        : String(Number(B[i].total));

    if (at !== bt) return false;
  }

  return true;
}

export default function WrapCashflowModal({
  open,
  initialRows = [],
  onClose,
  onSave,
}) {
  const [rows, setRows] = useState([makeRow()]);

  useEffect(() => {
    if (!open) return;

    setRows(normalizeIncomingRows(initialRows));
  }, [open, initialRows]);

  const normalizedForSave = useMemo(
    () => normalizeRowsForSave(rows),
    [rows]
  );

  const handleGridChange = useCallback((nextRows) => {
    setRows((prev) => {
      if (schedulesEquivalent(prev, nextRows)) return prev;

      return Array.isArray(nextRows) && nextRows.length
        ? nextRows
        : [makeRow()];
    });
  }, []);

  const handleAddBelow = useCallback((row) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === row.id);

      const updated = [...prev];

      updated.splice(idx + 1, 0, makeRow({}, idx + 1));

      return updated;
    });
  }, []);

  const handleDeleteRow = useCallback((id) => {
    setRows((prev) => {
      const updated = prev.filter((r) => r.id !== id);

      return updated.length ? updated : [makeRow()];
    });
  }, []);

  const columns = useMemo(
    () => [
      {
        key: "date",
        label: "Date",
        editable: true,
        date: true,
        editor: "date",
        className: "numeric",
        width: "min-w-[150px]",
      },
      {
        key: "total",
        label: "Total",
        editable: true,
        numeric: true,
        className: "text-right numeric",
        headerClassName: "text-right",
        width: "min-w-[180px]",
        format: (v) =>
          v != null && v !== ""
            ? Number(v).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "",
      },
      {
        key: "actions",
        label: "",
        width: "w-[84px]",
        headerClassName: "text-center",
        render: (row) => (
          <div className="flex items-center justify-center gap-1">
            <button
              type="button"
              onClick={() => handleAddBelow(row)}
              className="text-green-600 hover:text-green-800"
              title="Add row"
            >
              <Plus size={16} />
            </button>

            <button
              type="button"
              onClick={() => handleDeleteRow(row.id)}
              className="text-red-500 hover:text-red-700"
              title="Delete row"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ),
        copyValue: () => "",
      },
    ],
    [handleAddBelow, handleDeleteRow]
  );

  const handleClear = () => {
    setRows([makeRow()]);
  };

  const handleSave = () => {
    onSave?.(normalizedForSave);
    onClose?.();
  };

  if (!open) return null;

  return (
    <ModalBackdrop>
      <div
        className="w-full max-w-xl max-h-[85vh] flex flex-col rounded-2xl border border-border bg-app text-app shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wrap-cashflow-title"
      >

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div id="wrap-cashflow-title" className="text-lg font-semibold">
            Existing Debt Service
          </div>

          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="px-5 py-4 flex-1 min-h-0 overflow-y-auto">
          <div className="overflow-x-auto">
            <ExcelGrid
              columns={columns}
              rows={rows}
              onChange={handleGridChange}
              showCopyButton={true}
              allowPasteAppendRows={true}
              makePasteRow={(partial = {}, i = 0) =>
                makeRow(
                  {
                    date: partial.date ?? "",
                    total:
                      partial.total != null ? partial.total : "",
                  },
                  i
                )
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-border bg-app px-5 py-4">
          <button className="btn btn-ghost" onClick={handleClear}>
            Clear All
          </button>

          <div className="flex items-center justify-end gap-2">
            <button className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>

            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}
